import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../integrations/supabase/client';
import { toast } from 'sonner';

export interface Vehicle {
  id: string;
  name: string;
  type: string;
  category: string;
  base_fare: number;
  price_per_km: number;
  capacity: number;
  luggage_capacity: number;
  description: string | null;
  image_url: string | null;
  features: string[] | null;
  is_available: boolean;
  created_at: string;
  updated_at: string;
}

export const useVehicles = () => {
  return useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('is_available', true)
        .order('created_at', { ascending: false });
      
      if (error) {
        toast.error('Failed to load vehicles');
        throw error;
      }
      
      return data as Vehicle[];
    },
  });
};

export const useAllVehicles = () => {
  return useQuery({
    queryKey: ['all-vehicles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        toast.error('Failed to load vehicles');
        throw error;
      }
      
      return data as Vehicle[];
    },
  });
};

export const useCreateVehicle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (vehicle: Omit<Vehicle, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('vehicles')
        .insert(vehicle)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['all-vehicles'] });
      toast.success('Vehicle created successfully');
    },
    onError: () => {
      toast.error('Failed to create vehicle');
    },
  });
};

export const useUpdateVehicle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...vehicle }: Partial<Vehicle> & { id: string }) => {
      const { data, error } = await supabase
        .from('vehicles')
        .update(vehicle)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['all-vehicles'] });
      toast.success('Vehicle updated successfully');
    },
    onError: () => {
      toast.error('Failed to update vehicle');
    },
  });
};

export const useDeleteVehicle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('vehicles')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['all-vehicles'] });
      toast.success('Vehicle deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete vehicle');
    },
  });
};
